import { useEffect, useRef, useState } from "react";

/**
 * Generic hook for debounced or controlled fields with Redux/async updates
 *
 * @param valueFromStore - value from store/props
 * @param buildPayload - function that takes localValue and returns update payload
 * @param updateFn - function that handles the update (debounced outside if needed)
 */
export const useReduxField = <T, Payload>(
    valueFromStore: T,
    buildPayload: (value: T) => Payload,
    updateFn: (data: Payload) => void
) => {
    const [localValue, setLocalValue] = useState<T>(valueFromStore);
    const lastSent = useRef<T>(valueFromStore);

    // sync redux → local
    useEffect(() => {
        setLocalValue(valueFromStore);
        lastSent.current = valueFromStore;
    }, [valueFromStore]);

    // push local → redux
    useEffect(() => {
        if (Object.is(localValue, lastSent.current)) return;

        lastSent.current = localValue;
        updateFn(buildPayload(localValue));
    }, [localValue, buildPayload, updateFn]);

    return [localValue, setLocalValue] as const;
};